/**
 * chrz-core bootstrap — Alpine.js + HTMX initialisation.
 *
 * This is the single enqueue point for both libraries.
 * Plugins consume Alpine and htmx via window.Chrz (never via direct imports).
 */

import Alpine from 'alpinejs';
import mask from '@alpinejs/mask';
import intersect from '@alpinejs/intersect';
import htmx from 'htmx.org';

// ─── Alpine Setup ─────────────────────────────────────────────────────────────
Alpine.plugin(mask);
Alpine.plugin(intersect);
Alpine.start();

// ─── HTMX Setup ───────────────────────────────────────────────────────────────

// Apply WordPress REST nonce and CSRF token on every HTMX request.
// Both values are injected by the theme into <meta> tags in <head>.
const getNonce = () =>
  document.querySelector('meta[name="chrz-rest-nonce"]')?.content ?? '';

const getCsrfToken = () =>
  document.querySelector('meta[name="chrz-csrf-token"]')?.content ?? '';

document.addEventListener('htmx:configRequest', (event) => {
  event.detail.headers['X-WP-Nonce'] = getNonce();
  event.detail.headers['X-Chrz-CSRF'] = getCsrfToken();
});

// Surface response errors to the user and report to the server.
document.addEventListener('htmx:responseError', (event) => {
  const status = event.detail.xhr?.status;

  chrz_showToast(
    window.Chrz?.i18n?.error ?? 'خطایی رخ داد. لطفاً دوباره تلاش کنید.',
    'error',
  );

  // No-op if the client-log endpoint does not yet exist (404 is expected until that change ships).
  if (status === 404) return;

  fetch('/wp-json/chrz/v1/_log/client', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': getNonce() },
    body: JSON.stringify({ status, url: event.detail.xhr?.responseURL }),
  }).catch(() => {/* swallow — logging is best-effort */});
});

// ─── Toast Helper (used by the error handler above) ───────────────────────────
function chrz_showToast(message, type = 'info') {
  const container = document.getElementById('chrz-toast-container')
    ?? createToastContainer();

  const toast = document.createElement('div');
  toast.className = `chrz-toast chrz-toast--${type}`;
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  toast.textContent = message;

  container.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('chrz-toast--visible'));

  setTimeout(() => {
    toast.classList.remove('chrz-toast--visible');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
  }, 4000);
}

function createToastContainer() {
  const el = document.createElement('div');
  el.id = 'chrz-toast-container';
  el.className = 'chrz-toast-container';
  document.body.appendChild(el);
  return el;
}

// ─── Global Namespace ─────────────────────────────────────────────────────────
window.Chrz = Object.freeze({
  Alpine,
  htmx,
  showToast: chrz_showToast,
  i18n: window.Chrz?.i18n ?? {},
});
